import type { ReactElement, ReactNode } from 'react';
import { Check } from 'lucide-react';

import useLoggedInUser from 'app/hooks/useLoggedInUser';
import bookConsultationUrl from 'app/utils/bookConsultationUrl';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { RequestPlusButton, RequestPlusModal, useRequestPlusModal } from '../RequestPlusModal';

interface PlusPaywallImage {
  src: string;
  alt?: string;
}

interface PlusPaywallProps {
  title: string;
  description: string;
  benefits?: string[];
  image?: PlusPaywallImage;
  illustration?: ReactNode;
  benefitColumns?: 1 | 2;
  organisationId?: string;
  children?: ReactNode;
}

function PaywallImage({ src, alt = '' }: PlusPaywallImage): ReactElement {
  return (
    <div className="vtwo:w-full vtwo:lg:w-[500px] vtwo:shrink-0 vtwo:flex vtwo:items-center vtwo:bg-linear-to-r vtwo:from-[#E3EBFE] vtwo:to-[#D9EDFD]">
      <img
        src={src}
        alt={alt}
        width="500"
        height="375"
        loading="lazy"
        className="vtwo:w-full vtwo:max-w-[500px] vtwo:mx-auto vtwo:h-auto"
      />
    </div>
  );
}

export default function PlusPaywall({
  title,
  description,
  benefits = [],
  image,
  illustration,
  benefitColumns = 1,
  organisationId,
  children,
}: PlusPaywallProps): ReactElement {
  const requestPlusModal = useRequestPlusModal();
  const user = useLoggedInUser();

  return (
    <section className="vtwo:flex vtwo:flex-col vtwo:lg:flex-row vtwo:w-full vtwo:min-h-[440px] vtwo:rounded-3xl vtwo:overflow-hidden">
      <div className="vtwo:flex-1 vtwo:min-w-0 vtwo:flex vtwo:flex-col vtwo:justify-center vtwo:gap-5 vtwo:p-10 vtwo:bg-linear-to-r vtwo:from-[#F2E6FE] vtwo:to-[#E3EBFE]">
        <p className="vtwo:text-lg vtwo:font-semibold vtwo:text-black">
          Unlock with Healthera <span className="vtwo:text-featured">Plus+</span>
        </p>
        <h2 className="vtwo:text-4xl vtwo:font-bold vtwo:leading-tight vtwo:text-black">{title}</h2>
        <p className="vtwo:text-lg vtwo:text-gray-800 vtwo:max-w-2xl">{description}</p>
        {benefits.length > 0 && (
          <ul
            className={cn(
              'vtwo:grid vtwo:gap-x-8 vtwo:gap-y-2 vtwo:list-none vtwo:p-0 vtwo:m-0',
              benefitColumns === 2 && 'vtwo:md:grid-cols-2'
            )}
          >
            {benefits.map((benefit) => (
              <li key={benefit} className="vtwo:flex vtwo:items-center vtwo:gap-3">
                <Check className="vtwo:size-5 vtwo:shrink-0 vtwo:text-black" aria-hidden="true" />
                <span className="vtwo:text-lg vtwo:text-gray-800">{benefit}</span>
              </li>
            ))}
          </ul>
        )}
        {children}
        <div className="vtwo:flex vtwo:flex-wrap vtwo:gap-4 vtwo:pt-2">
          <RequestPlusButton
            size="xl"
            className="vtwo:bg-teal-900 vtwo:text-white vtwo:hover:bg-teal-950 vtwo:rounded-lg vtwo:px-6"
            onClick={requestPlusModal.open}
            organisationId={organisationId}
          >
            Request Upgrade
          </RequestPlusButton>
          <Button
            asChild
            variant="outline"
            size="xl"
            className="vtwo:border-black vtwo:bg-transparent vtwo:text-black vtwo:rounded-lg vtwo:px-6 vtwo:no-underline"
          >
            <a href={bookConsultationUrl(user?.username)} target="_blank" rel="noopener noreferrer">
              Book consultation
            </a>
          </Button>
        </div>
      </div>
      {image && <PaywallImage src={image.src} alt={image.alt} />}
      {illustration && (
        <div
          aria-hidden="true"
          className="vtwo:lg:w-[45%] vtwo:shrink-0 vtwo:min-w-0 vtwo:flex vtwo:items-center vtwo:justify-center"
        >
          {illustration}
        </div>
      )}
      <RequestPlusModal
        open={requestPlusModal.isOpen}
        onClose={requestPlusModal.close}
        organisationId={organisationId}
      />
    </section>
  );
}
